import { ref, reactive, onUnmounted, type Ref, type Reactive } from 'vue';

type CountdownRemTime = {
    seconds: string;
    minutes: string;
    hours: string;
    days: string;
};

type UseCountdownReturn = {
    remTime: Reactive<CountdownRemTime>;
    expired: Ref<boolean>;
    updateCountdown: (newTime?: number) => void;
};

function useCountdown(timestamp?: number): UseCountdownReturn {
    const remTime = reactive<CountdownRemTime>({
        seconds: '00',
        minutes: '00',
        hours: '00',
        days: '0',
    });

    const expired = ref<boolean>(true);

    let interval: ReturnType<typeof setInterval> | null = null;

    function calculateRemainingTime(newTime: number): void {
        const currDate = Date.now();
        const diff = newTime - currDate;

        if (diff <= 0) {
            expired.value = true;
            if (interval !== null) {
                clearInterval(interval);
                interval = null;
            }
        }

        remTime.seconds = toPadString(
            diff > 0 ? Math.floor((diff / 1000) % 60) : 0
        );
        remTime.minutes = toPadString(
            diff > 0 ? Math.floor((diff / 1000 / 60) % 60) : 0
        );
        remTime.hours = toPadString(
            diff > 0 ? Math.floor((diff / (1000 * 60 * 60)) % 24) : 0
        );
        remTime.days = String(
            diff > 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) : 0
        );
    }

    function updateCountdown(newTime?: number) {
        if (newTime === undefined) {
            return;
        }

        expired.value = false;
        if (interval !== null) {
            clearInterval(interval);
            interval = null;
        }

        // Calculate immediately, not wait for first interval tick
        calculateRemainingTime(newTime);

        interval = setInterval(() => {
            calculateRemainingTime(newTime);
        }, 1000);
    }

    if (timestamp !== undefined) {
        updateCountdown(timestamp);
    }

    onUnmounted(() => {
        if (interval !== null) {
            clearInterval(interval);
            interval = null;
        }
    });

    function toPadString(n: number): string {
        return String(n).padStart(2, '0');
    }

    return {
        expired,
        remTime,
        updateCountdown,
    };
}

export { useCountdown };
